import React, { Suspense, useEffect } from 'react';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { Auth0User } from 'types';
import { currentRelativePath, extractUserId } from 'auth/util';
import loader from 'ui/illustrations/CW_line_loader.svg';
import { SWRConfig } from 'swr';
import { getFetcher } from 'apis/utils';
import Navigation, { NavigationContextProvider } from 'domain/shared/Navigation';
import LoadingBar from 'ui/elements/Loading/LoadingBar';
import { useConfigureAccessToken } from './useConfigureAccessToken';
import { useSelfUserProfile } from 'apis/CompanyAPI/users/useSelfUserProfile';
import { useIntercomActions } from 'contexts/IntercomContext';
import { setGcpUserId } from 'util/gcpLogging';
import SessionExpiredScreen from 'auth/SessionExpiredScreen';

export const GET_NEW_TOKEN_BEFORE_EXPIRATION_MINUTES = 1;

export function LoadingScreen() {
  return (
    <div className="u-flex-center u-flex-align-center" style={{ height: '80vh' }}>
      <img style={{ height: '10vh', maxHeight: 80, minHeight: 50 }} src={loader} />
    </div>
  );
}

function AuthenticatedRoutes({ children }: { children: React.ReactNode }) {
  const { user } = useAuth0();
  const userId = extractUserId(user as Auth0User);

  const { accessTokenState, isAccessTokenReady } = useConfigureAccessToken();
  const { resource: userProfileResource } = useSelfUserProfile();
  const { updateUser } = useIntercomActions();

  useEffect(() => {
    // Set user ID for GCP Error Reporting
    setGcpUserId(userId?.toString());
  }, [isAccessTokenReady, userId]);

  // Update Intercom with user data whenever user profile is fetched
  useEffect(() => {
    if (userProfileResource.state === 'fetched') {
      updateUser(userProfileResource.resource);
    }
  }, [userProfileResource.state]);

  // A failed token fetch used to fall through to the loading screen forever. withAuthenticationRequired
  // can't rescue it either: it only redirects when isAuthenticated is false, and a cached login in
  // localStorage keeps that true long after the Auth0 session is gone.
  if (accessTokenState.status === 'failed') {
    return <SessionExpiredScreen failure={accessTokenState.failure} />;
  }

  if (!isAccessTokenReady) {
    return <LoadingScreen />;
  }

  return (
    <div className="u-flex-space-between" style={{ height: '100%' }}>
      <SWRConfig
        value={{
          fetcher: getFetcher,
        }}
      >
        <NavigationContextProvider>
          <Navigation />
          {/* If you remove this <Suspense> your are officially an idiot: https://github.com/mui/material-ui/issues/33748 */}
          <Suspense fallback={<LoadingBar isComplete={false} />}>{children}</Suspense>
        </NavigationContextProvider>
      </SWRConfig>
    </div>
  );
}

function OnRedirecting() {
  return <LoadingScreen />;
}

export default withAuthenticationRequired(AuthenticatedRoutes, {
  returnTo: () => currentRelativePath(),
  onRedirecting: OnRedirecting,
});
